import { Button, FormLayout, TextInput } from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { getInputErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import { powerBiConfigFields } from './utils'
import {
  powerBiConfigSchema,
  PowerBiConfigFormFields,
} from 'utils/schema/powerBiConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  triggerPowerBiManualSync,
  triggerPowerBiRmaManualSync,
  updatePowerBiConfig,
} from 'utils/api/configApi'

const { ORDER_URL, TOKEN, IS_ENABLED } = powerBiConfigFields

type Props = {
  configuration?: ConfigurationWithId
}

const PowerBiConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PowerBiConfigFormFields>({
    resolver: yupResolver(powerBiConfigSchema),
    defaultValues: { ...(configuration?.powerBi || {}) },
  })

  const [loading, setLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [rmaSyncLoading, setRmaSyncLoading] = useState(false)

  const handleSave = async (data: PowerBiConfigFormFields) => {
    const configToast = toast.loading('Updating Power BI config...')
    setLoading(true)
    try {
      await updatePowerBiConfig(data)
      toast.success('Power BI config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Power BI config.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleManualSync = async () => {
    const syncToast = toast.loading('Triggering Power BI manual sync...')
    setSyncLoading(true)
    try {
      await triggerPowerBiManualSync()
      toast.success('Power BI manual sync triggered successfully.', {
        id: syncToast,
      })
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to trigger Power BI manual sync.',
      })
    } finally {
      setSyncLoading(false)
    }
  }

  const handleRmaManualSync = async () => {
    const syncToast = toast.loading('Triggering Power BI RMA manual sync...')
    setRmaSyncLoading(true)
    try {
      await triggerPowerBiRmaManualSync()
      toast.success('Power BI RMA manual sync triggered successfully.', {
        id: syncToast,
      })
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to trigger Power BI RMA manual sync.',
      })
    } finally {
      setRmaSyncLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= INTEGRATION ================= */}
      <h3 className="title mt-4">Integration Configuration</h3>

      <FormLayout>
        {/* Top Section: Enable Toggle */}
        <SwitchField
          control={control}
          name={IS_ENABLED.key}
          label={IS_ENABLED.label}
          description="Enable or disable Power BI order sync integration"
          showStatusPill
        />

        <div className="mb-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={ORDER_URL.key}
            label={ORDER_URL.label}
            error={getInputErrorMessage(errors, ORDER_URL)}
            placeholder="https://..."
          />

          <TextInput
            required
            type="password"
            register={register}
            name={TOKEN.key}
            label={TOKEN.label}
            error={getInputErrorMessage(errors, TOKEN)}
            placeholder="Bearer token"
          />
        </div>
      </FormLayout>

      <div className="mt-5 flex items-center gap-3">
        <Button loading={loading} buttonText="Save" className="w-32" />

        <Button
          type="button"
          variant="outline"
          loading={syncLoading}
          disabled={syncLoading || loading}
          buttonText="Trigger Order Sync"
          className="w-48"
          onClick={handleManualSync}
        />

        <Button
          type="button"
          variant="outline"
          loading={rmaSyncLoading}
          disabled={rmaSyncLoading || loading}
          buttonText="Trigger RMA Sync"
          className="w-48"
          onClick={handleRmaManualSync}
        />
      </div>
    </form>
  )
}

export default PowerBiConfigurationForm
